import type { Project } from '@hangar/shared';
import { CompactTable, type CompactColumn } from '@/components/CompactTable';
import type { RowAction } from '@/components/RowActions';
import { EnvPill } from '@/components/badges';
import { appsLabel, compactEnvironments, environmentsLine } from './projectsModel';

interface Props {
  projects: Project[];
  /** Live apps per project id; undefined while counting. */
  appCounts: Record<string, number | undefined>;
  selectedId: string | undefined;
  /** Makes this project the one the page is about. */
  onSelect: (project: Project) => void;
  actionsFor: (project: Project) => RowAction[];
}

type NameProps = Pick<Props, 'selectedId' | 'onSelect'> & { project: Project };

function ProjectNameButton({ project: p, selectedId, onSelect }: NameProps) {
  return (
    <button type="button" data-testid={`gs-project-select-${p.slug}`} aria-pressed={p.id === selectedId} onClick={() => onSelect(p)} className="flex min-w-0 max-w-full flex-col rounded-sm text-left">
      <span className="truncate text-[13px] font-medium text-foreground underline-offset-4 hover:underline" title={p.name}>
        {p.name}
      </span>
      <span className="truncate font-mono text-xs text-subtle">{p.slug}</span>
    </button>
  );
}

function ProjectCell(props: NameProps) {
  return (
    <>
      <ProjectNameButton {...props} />
      {/* Narrow cards fold the environments column into this cell. */}
      <span className="mt-1 hidden truncate font-mono text-[11px] text-subtle @max-[32rem]:block">{environmentsLine(props.project)}</span>
    </>
  );
}

function EnvironmentsCell({ project }: { project: Project }) {
  const envs = compactEnvironments(project);
  return (
    <span className="flex flex-wrap gap-1">
      {envs.length ? envs.map((e) => <EnvPill key={e.id} env={e} />) : <span className="text-xs text-subtle">none</span>}
    </span>
  );
}

function PhoneProjectRow({ appCount, ...name }: NameProps & { appCount: number | undefined }) {
  return (
    <span className="flex min-w-0 flex-col gap-1.5">
      <ProjectNameButton {...name} />
      <span className="flex flex-wrap items-center gap-1">
        {compactEnvironments(name.project).map((e) => (
          <EnvPill key={e.id} env={e} />
        ))}
        <span className="font-mono text-xs text-subtle">· {appsLabel(appCount)}</span>
      </span>
    </span>
  );
}

function projectColumns({ appCounts, selectedId, onSelect }: Props): CompactColumn<Project>[] {
  return [
    { key: 'project', header: 'Project', cell: (p) => <ProjectCell project={p} selectedId={selectedId} onSelect={onSelect} />, className: 'max-w-0', width: 'w-[30%]' },
    { key: 'envs', header: 'Environments', cell: (p) => <EnvironmentsCell project={p} />, className: 'whitespace-normal', hide: '@max-[32rem]:hidden' },
    { key: 'apps', header: 'Apps', cell: (p) => <span className="tabular font-mono text-xs text-muted-foreground">{appsLabel(appCounts[p.id])}</span> },
  ];
}

/** Step 2's table: project (select), environments, apps, Edit · Archive. */
export function ProjectsTable(props: Props) {
  const { projects, appCounts, selectedId, onSelect, actionsFor } = props;
  return (
    <CompactTable
      label="Projects"
      rows={projects}
      columns={projectColumns(props)}
      rowKey={(p) => p.id}
      rowTestId={(p) => `gs-project-${p.slug}`}
      subject={(p) => p.name}
      actions={actionsFor}
      selected={(p) => p.id === selectedId}
      phoneRow={(p) => <PhoneProjectRow project={p} selectedId={selectedId} onSelect={onSelect} appCount={appCounts[p.id]} />}
    />
  );
}
